// The deck on a phone held upright (DeckViewport and
// reports/slides/deck_shell+phone): one board a page, paged by a tap on
// either side or a swipe (touch.js), a hairline for each chapter over it,
// the chapters as a sheet in place of the overview, and the board's back
// as a sheet over the page. Turned on its side, the phone asks for the
// board itself; upright again, for the phone's pages.
//
// A phone that was served the board without asking for it — a browser the
// server did not know for a phone — asks for the phone's pages too.

const NARROW = 640;
// The left third of the page goes back, the rest forward: the thumb rests
// on the right.
const BACK_SHARE = 1 / 3;
// A phone on its side: a desktop window asked for the phone's pages is
// wider than it is tall too, and keeps them.
const SIDEWAYS = `(orientation: landscape) and (max-height: ${NARROW}px)`;

export default {
  connectPhone() {
    this.portrait = window.matchMedia(
      `(orientation: portrait) and (max-width: ${NARROW}px)`,
    );
    this.boundOrientation = () => this.followOrientation();
    this.portrait.addEventListener("change", this.boundOrientation);
    if (!this.phoneValue) {
      this.followOrientation();
      return;
    }

    this.boundTap = this.handleTap.bind(this);
    this.stageTarget.addEventListener("click", this.boundTap);
    this.countChapters();
    this.followOrientation();
  },

  disconnectPhone() {
    this.portrait?.removeEventListener("change", this.boundOrientation);
    if (this.boundTap)
      this.stageTarget.removeEventListener("click", this.boundTap);
  },

  // Upright and narrow is the phone's pages; anything else is the board.
  // Only a board view the reader asked for by name stays the board when
  // the phone is upright: "View as board" from the chapters.
  followOrientation() {
    if (this.remoteValue || this.presenterValue) return;
    const url = new URL(window.location.href);
    const upright = this.portrait.matches;
    if (this.phoneValue && !upright && window.matchMedia(SIDEWAYS).matches) {
      url.searchParams.set("view", "board");
      url.searchParams.set("from", "phone");
      window.location.replace(url.toString());
    } else if (
      !this.phoneValue &&
      upright &&
      (!url.searchParams.has("view") ||
        url.searchParams.get("from") === "phone")
    ) {
      url.searchParams.set("view", "phone");
      url.searchParams.delete("from");
      window.location.replace(url.toString());
    }
  },

  // A tap on the page turns it, unless it was on something to press or the
  // reader was selecting text.
  handleTap(event) {
    // Turned over, a tap beside the back's sheet puts the board back.
    if (this.turned) {
      if (!event.target.closest(".deck-back, button")) this.turnOver();
      return;
    }
    if (
      event.target.closest(
        "a, button, input, label, select, summary, .deck-back",
      )
    )
      return;
    if (String(window.getSelection?.() || "").trim()) return;
    const box = this.stageTarget.getBoundingClientRect();
    if (event.clientX - box.left < box.width * BACK_SHARE) this.previous();
    else this.next();
  },

  // After every page turn (showSlide): the page starts at its top, the
  // board on it plays its motion (deck/motion.css hangs off the board's
  // own `.is-current`), and the chapter hairlines fill to the board on show.
  renderPhone(index) {
    this.slideTargets.forEach((page, i) => {
      page
        .querySelector(".deck-phone-plate > .deck-board")
        ?.classList.toggle("is-current", i === index);
    });
    const scroll =
      this.slideTargets[index]?.querySelector(".deck-phone-scroll");
    if (scroll) scroll.scrollTop = 0;

    // A drawn page on a coloured ground (the revenue hero's black plate)
    // takes the deck's head and foot onto that ground with it.
    const ground = [...(this.slideTargets[index]?.classList || [])].find(
      (name) => name.startsWith("deck-ground--"),
    );
    [...this.element.classList]
      .filter((name) => name.startsWith("deck-ground--"))
      .forEach((name) => this.element.classList.remove(name));
    if (ground) this.element.classList.add(ground);

    const visible = this.visibleIndices();
    const chapter = this.slideTargets[index]?.dataset.chapter;
    const keys = this.segmentTargets.map((segment) => segment.dataset.chapter);
    const at = keys.indexOf(chapter);
    this.segmentTargets.forEach((segment, i) => {
      let share = 0;
      if (at >= 0 && i < at) share = 1;
      if (i === at) {
        const boards = visible.filter(
          (v) => this.slideTargets[v].dataset.chapter === chapter,
        );
        share = (boards.indexOf(index) + 1) / boards.length;
      }
      segment.firstElementChild.style.width = `${(share * 100).toFixed(1)}%`;
    });
  },

  // How many boards on show each chapter holds; a chapter with none is not
  // offered.
  countChapters() {
    const visible = this.visibleIndices();
    this.chapterCountTargets.forEach((count) => {
      const n = visible.filter(
        (i) => this.slideTargets[i].dataset.chapter === count.dataset.chapter,
      ).length;
      count.textContent =
        n === 1
          ? count.dataset.one
          : count.dataset.other.replace("%{count}", n);
      const row = count.closest("li");
      if (row) row.hidden = n === 0;
    });
  },

  openChapters() {
    if (!this.hasChaptersSheetTarget) return;
    this.countChapters();
    const current = this.slideTargets[this.indexValue]?.dataset.chapter;
    this.chaptersSheetTarget
      .querySelectorAll(".deck-phone-chapter")
      .forEach((button) => {
        if (button.dataset.chapter === current)
          button.setAttribute("aria-current", "true");
        else button.removeAttribute("aria-current");
      });
    this.chaptersSheetTarget.hidden = false;
    this.chaptersSheetTarget
      .querySelector(".deck-phone-sheet-head button")
      ?.focus();
  },

  closeChapters() {
    if (this.hasChaptersSheetTarget) this.chaptersSheetTarget.hidden = true;
  },

  // The board, on the board view, at the board on show.
  viewBoard(event) {
    event.preventDefault();
    const url = new URL(event.currentTarget.href);
    url.hash = this.slideRef(this.indexValue);
    window.location.assign(url.toString());
  },

  // The phone's own share sheet, at this board: the deck's share link where
  // there is one, otherwise this address, which asks a colleague to log in.
  async shareBoard(event) {
    const button = event.currentTarget;
    const url = new URL(
      this.element.dataset.presentationShareUrl || window.location.href,
    );
    url.searchParams.delete("from");
    url.hash = this.slideRef(this.indexValue);
    if (navigator.share) {
      try {
        await navigator.share({ title: document.title, url: url.toString() });
      } catch {
        // Closed without sharing.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url.toString());
      const label = button.getAttribute("aria-label");
      button.setAttribute("aria-label", button.dataset.copiedLabel);
      button.classList.add("is-copied");
      setTimeout(() => {
        button.setAttribute("aria-label", label);
        button.classList.remove("is-copied");
      }, 1600);
    } catch {
      // No clipboard either: nothing to do.
    }
  },
};
